/**
 * Friend Inbox view — declarative React component.
 *
 * Elm-ish architecture: the view is mostly declarative. It reads from
 * the model and dispatches messages. It does NOT own state, does NOT
 * perform I/O, and does NOT make authorization decisions.
 *
 * Backend is authoritative; UI is for affordance.
 */
import { useFriendInboxProgram } from './useFriendInboxProgram';
import { isPendingStatus } from './model';
import type { FriendInboxMsg } from './messages';

/**
 * Friend Inbox page.
 *
 *   U1: Friend lists incoming intro requests
 *   U2: Friend inspects an intro request
 *   U3: Friend accepts a pending intro request
 *   U4: Friend rejects a pending intro request
 *   U5: After a transition, the inbox list is refreshed so the row
 *       reflects the canonical server status. This page is Friend-
 *       facing; the Sponsor observes the canonical state via the
 *       existing Sponsor-side intro request flow.
 */
export function FriendInboxPage() {
  const { model, dispatch } = useFriendInboxProgram();

  if (model.load === 'idle' || model.load === 'loading') {
    return <div role="status">Loading inbox...</div>;
  }

  if (model.load === 'error') {
    return (
      <div role="alert">
        <p>Could not load inbox: {model.error ?? 'unknown error'}</p>
        <button onClick={() => dispatch({ type: 'RefreshRequested' } satisfies FriendInboxMsg)}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <main>
      <h1>Intro requests</h1>

      {model.load === 'empty' && <p>No incoming intro requests.</p>}

      {model.load === 'ready' && (
        <ul aria-label="incoming intro requests">
          {model.items.map((it) => (
            <li key={it.id}>
              <button
                onClick={() =>
                  dispatch({ type: 'ItemSelected', id: it.id } satisfies FriendInboxMsg)
                }
              >
                {it.created_at} — status: {it.status}
                {it.message ? ` — ${it.message.slice(0, 60)}` : ''}
              </button>
            </li>
          ))}
        </ul>
      )}

      {model.selected && (
        <section aria-label="intro request detail">
          <h2>Intro request {model.selected.id}</h2>
          <p>
            Status: <strong>{model.selected.status}</strong>
          </p>
          <p>Created at: {model.selected.created_at}</p>
          {model.selected.decided_at && <p>Decided at: {model.selected.decided_at}</p>}
          {model.selected.message && <p>Message: {model.selected.message}</p>}
          {isPendingStatus(model.selected.status) && (
            <div>
              <button
                onClick={() =>
                  dispatch({
                    type: 'AcceptRequested',
                    id: model.selected!.id,
                  } satisfies FriendInboxMsg)
                }
                disabled={model.transitioning !== null}
              >
                Accept
              </button>
              <button
                onClick={() =>
                  dispatch({
                    type: 'RejectRequested',
                    id: model.selected!.id,
                  } satisfies FriendInboxMsg)
                }
                disabled={model.transitioning !== null}
              >
                Reject
              </button>
            </div>
          )}
          <button
            onClick={() => dispatch({ type: 'SelectionCleared' } satisfies FriendInboxMsg)}
          >
            Close
          </button>
        </section>
      )}
    </main>
  );
}
